/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import DeveloperOptionsStore from '@app/stores/DeveloperOptionsStore';
import * as ImageCacheUtils from '@app/utils/ImageCacheUtils';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {thumbHashToDataURL} from 'thumbhash';

export interface MediaLoadingState {
	loaded: boolean;
	error: boolean;
	thumbHashURL?: string;
	onLoad: () => void;
	onError: () => void;
}

export function useMediaLoading(src: string, placeholder?: string): MediaLoadingState {
	const [loadingState, setLoadingState] = useState<Pick<MediaLoadingState, 'error' | 'loaded'>>({
		loaded: ImageCacheUtils.hasImage(src),
		error: false,
	});
	const shouldForcePlaceholder =
		DeveloperOptionsStore.forceRenderPlaceholders || DeveloperOptionsStore.forceMediaLoading;

	const thumbHashURL = useMemo(() => {
		if (!placeholder) return;
		try {
			const bytes = Uint8Array.from(atob(placeholder), (c) => c.charCodeAt(0));
			return thumbHashToDataURL(bytes);
		} catch {
			return;
		}
	}, [placeholder]);

	useEffect(() => {
		const isCached = ImageCacheUtils.hasImage(src);
		if (shouldForcePlaceholder) {
			setLoadingState({loaded: false, error: false});
			return;
		}
		setLoadingState({loaded: isCached, error: false});
		if (isCached) return;

		let isCurrent = true;

		ImageCacheUtils.loadImage(
			src,
			() => {
				if (!isCurrent) return;
				setLoadingState({loaded: true, error: false});
			},
			() => {
				if (!isCurrent) return;
				setLoadingState((currentState) => (currentState.loaded ? currentState : {loaded: false, error: true}));
			},
		);

		return () => {
			isCurrent = false;
		};
	}, [shouldForcePlaceholder, src]);

	const onLoad = useCallback(() => {
		setLoadingState({loaded: true, error: false});
	}, []);

	const onError = useCallback(() => {
		setLoadingState((prev) => (prev.loaded ? prev : {loaded: false, error: true}));
	}, []);

	return {...loadingState, thumbHashURL, onLoad, onError};
}
